
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActivation } from '@/contexts/ActivationContext';
import { useAuth } from '@/contexts/AuthContext';
import { getEndpointUrl } from '@/lib/api-config';
import { formatRut } from '@/lib/rut';

type FormMode = 'loading' | 'view' | 'edit' | 'new';

export default function SIIConnectionForm() {
  const navigate = useNavigate();
  const { isStepComplete, refreshStatus, isLoading: activationLoading } = useActivation();
  const { refreshUser } = useAuth();
  const isCompleted = isStepComplete('conexionSii');

  const [mode, setMode] = useState<FormMode>('loading');
  const [rut, setRut] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (activationLoading) {
      setMode('loading');
      return;
    }

    if (!isCompleted) {
      setMode('new');
      return;
    }

    const loadCredentials = async () => {
      try {
        const url = getEndpointUrl('sii', 'get');
        const { httpClient } = await import('@/lib/http-client');
        // El backend solo devuelve `rut`. La clave nunca se expone en la API
        // del comercio: el campo se mantiene vacío en modo view (placeholder).
        const data = await httpClient.get<{ rut: string }>(url);
        setRut(data.rut);
        setPassword('');
        setMode('view');
      } catch {
        setLoadError('No se pudieron cargar las credenciales. Intenta nuevamente.');
        setMode('view');
      }
    };

    loadCredentials();
  }, [isCompleted, activationLoading]);

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRut(formatRut(e.target.value));
  };

  const handleEnableEdit = () => {
    setPassword('');
    setShowPassword(false);
    setError('');
    setMode('edit');
  };

  const handleCancelEdit = () => {
    // Vacía el campo en lugar de poner el literal '******': cuando el modo
    // entra a `view` el input ya muestra el placeholder de bullets nativo, y
    // así no se puede confundir el literal con el password real al re-editar.
    setPassword('');
    setShowPassword(false);
    setError('');
    setMode('view');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!rut || !password) {
      setError('Completa todos los campos');
      return;
    }
    setSubmitting(true);
    try {
      const { httpClient } = await import('@/lib/http-client');
      if (mode === 'edit') {
        const url = getEndpointUrl('sii', 'update');
        await httpClient.put(url, { rut, password });
        // El backend sincroniza Company.taxId con el RUT del SII; refrescar el user
        // del AuthContext para que el RUT del titular bancario lea el valor canónico.
        await Promise.all([refreshStatus(), refreshUser()]);
        setMode('view');
      } else {
        const url = getEndpointUrl('sii', 'save');
        await httpClient.post(url, { rut, password });
        await Promise.all([refreshStatus(), refreshUser()]);
        navigate('/configuraciones/resumen');
      }
    } catch (err: any) {
      setError(err?.message || 'Error al guardar las credenciales. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const isReadOnly = mode === 'view';
  const isLoading = mode === 'loading';

  return (
    <div className="mx-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[600px] flex items-center justify-center">
      <div className="w-full p-6 md:p-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-50 dark:bg-slate-800 text-primary mb-4 ring-1 ring-slate-100 dark:ring-slate-700">
            {isLoading ? (
              <span className="material-symbols-outlined text-3xl animate-spin text-slate-400">progress_activity</span>
            ) : isReadOnly ? (
              <span className="material-symbols-outlined text-3xl text-emerald-500">verified</span>
            ) : (
              <span className="material-symbols-outlined text-3xl">cloud_sync</span>
            )}
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
            {isReadOnly ? 'Cuenta SII vinculada' : 'Vincular cuenta SII'}
          </h2>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            {isReadOnly
              ? 'Tu cuenta del Servicio de Impuestos Internos está conectada correctamente.'
              : 'Conecta tu cuenta del Servicio de Impuestos Internos para automatizar la lectura de tu facturación.'}
          </p>
        </div>

        {loadError && (
          <div className="mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">warning</span>
            {loadError}
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-4">
            <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
            <div className="h-10 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
            <div className="h-11 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse mt-6" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} autoComplete="off" className="space-y-6">
            <div>
              <label
                htmlFor="rut"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                RUT de la Empresa
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-slate-400 text-xl">business</span>
                </div>
                <input
                  id="rut"
                  name="rut"
                  type="text"
                  value={rut}
                  onChange={handleRutChange}
                  placeholder="76.123.456-7"
                  readOnly={isReadOnly}
                  autoComplete="off"
                  className={`pl-10 block w-full rounded-lg border text-sm py-2.5 transition-colors
                    ${isReadOnly
                      ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-default select-none'
                      : 'border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-primary focus:ring-primary'
                    }`}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="sii-password"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                Clave Tributaria
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-slate-400 text-xl">lock</span>
                </div>
                <input
                  id="sii-password"
                  name="password"
                  type={isReadOnly || !showPassword ? 'password' : 'text'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  readOnly={isReadOnly}
                  autoComplete="new-password"
                  className={`pl-10 pr-10 block w-full rounded-lg border text-sm py-2.5 transition-colors
                    ${isReadOnly
                      ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 cursor-default select-none'
                      : 'border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:border-primary focus:ring-primary'
                    }`}
                />
                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <span className="material-symbols-outlined text-slate-400 text-[20px] hover:text-slate-600">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                )}
              </div>
            </div>

            {isReadOnly ? (
              <button
                type="button"
                onClick={handleEnableEdit}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-slate-300 dark:border-slate-600 rounded-lg shadow-sm text-sm font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 transition-all"
              >
                <span className="material-symbols-outlined text-base">edit</span>
                Editar credenciales
              </button>
            ) : (
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                      {mode === 'edit' ? 'Actualizando...' : 'Vinculando...'}
                    </span>
                  ) : (
                    mode === 'edit' ? 'Actualizar credenciales' : 'Vincular Cuenta'
                  )}
                </button>
                {mode === 'edit' && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="w-full flex justify-center py-2.5 px-4 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            )}
          </form>
        )}

      </div>
    </div>
  );
}
